import { MutableRefObject, RefCallback, useMemo } from "react";

import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { EditorState, EditorThemeClasses, Klass, LexicalEditor, LexicalNode, LexicalNodeReplacement } from "lexical";

import { CustomActionNameNode } from "./nodes/CustomActionNameNode";
import { CustomVariableNode } from "./nodes/CustomVariableNode";
import { EditablePlugin } from "./plugins/EditablePlugin/EditablePlugin";
import { EditorStatePlugin } from "./plugins/EditorStatePlugin";
import { InsertTextPlugin } from "./plugins/InsertTextPlugin";

import styles from "./RVLexicalEditor.module.scss";

/** Props for {@link RVLexicalEditor}. */
export type RVLexicalEditorProps = Omit<React.ComponentProps<typeof ContentEditable>, "placeholder" | "onChange"> & {
    /** A ref callback or mutable ref object that will be set to the Lexical editor instance. */
    editorRef: RefCallback<LexicalEditor> | MutableRefObject<LexicalEditor | null | undefined>;
    /** The initial serialized value of the editor (HTML or plain text). */
    value?: string;
    /** When `disabled` is true and `noInert` is also true, text remains selectable. */
    noInert?: boolean;
    /** Callback invoked with the new `EditorState` on every content change. */
    onChange?: (value: EditorState) => void;
    /** Render the editor in plain-text mode (no rich-text toolbar features). */
    isPlainText?: boolean;
    /** Placeholder element or factory function shown when the editor is empty. */
    placeholder?: ((isEditable: boolean) => null | JSX.Element) | null | JSX.Element;
    /** Test ID forwarded to the underlying `ContentEditable`. */
    "data-testid"?: string;
    /** Additional Lexical node classes to register beyond the built-in set. */
    nodeOverrides?: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>;
};

const onError = (error: unknown) => {
    // eslint-disable-next-line no-console
    console.error(error);
};

const theme: EditorThemeClasses = {
    customVariable: styles.customVariable,
    customActionNameVariable: styles.customActionNameVariable,
    paragraph: styles.paragraph,
    text: {
        bold: styles.textBold,
        italic: styles.textItalic,
        underline: styles.textUnderline,
    },
    list: {
        listitem: styles.listItem,
        nested: {
            listitem: styles.nestedListItem,
        },
        olDepth: [styles.ol1, styles.ol2, styles.ol3, styles.ol4, styles.ol5],
        ulDepth: [styles.ol1, styles.ol2, styles.ol3, styles.ol4, styles.ol5],
        ul: styles.unorderedList,
    },
    link: styles.link,
};

/**
 * A composable rich-text / plain-text editor built on Lexical.
 *
 * Provides a `LexicalComposer` context with sensible defaults (history, clear,
 * custom variable highlighting). Consumers pass children (plugins) to extend
 * behaviour.
 *
 * @example
 * ```tsx
 * <RVLexicalEditor editorRef={ref} onChange={handleChange}>
 *   <CustomVariablesHighlightPlugin />
 * </RVLexicalEditor>
 * ```
 */
export function RVLexicalEditor({
    editorRef,
    value,
    onChange,
    placeholder = null,
    className,
    "data-testid": dataTestId,
    isPlainText = false,
    children,
    disabled,
    noInert = false,
    nodeOverrides = [],
    ...rest
}: RVLexicalEditorProps) {
    const config = useMemo(
        () => ({
            namespace: "regal",
            theme,
            onError,
            editorState: value,
            nodes: [CustomVariableNode, CustomActionNameNode, ...nodeOverrides],
            editable: !disabled,
        }),
        [value, disabled, nodeOverrides]
    );

    const textPluginProps = useMemo(
        () => ({
            contentEditable: (
                <ContentEditable
                    className={className}
                    data-testid={dataTestId}
                    // when the editor is disabled
                    // we mark it as inert so it can't be focused
                    // this prevents a suite of errors and edge cases that we have
                    // including unwanted validation triggers and placeholder text updates
                    {...(disabled ? (noInert ? {} : { inert: "" }) : {})}
                    {...rest}
                />
            ),
            ErrorBoundary: LexicalErrorBoundary,
            placeholder,
        }),
        [className, dataTestId, disabled, noInert, placeholder, rest]
    );

    return (
        <LexicalComposer initialConfig={config}>
            {isPlainText ? <PlainTextPlugin {...textPluginProps} /> : <RichTextPlugin {...textPluginProps} />}
            <EditorRefPlugin editorRef={editorRef} />
            <EditablePlugin disabled={disabled} />
            <EditorStatePlugin onChange={onChange} />
            <HistoryPlugin />
            <InsertTextPlugin />
            <ClearEditorPlugin />

            {children}
        </LexicalComposer>
    );
}
